import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import { Account, Segment, SegmentSettings as SegmentSettingsType, SegmentSettingsUpdate } from '../../interfaces/accounting.interface';
import { SegmentSettingsService } from '../../services/segment-settings.service';
import { SegmentService } from '../../services/segment.service';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import SearchableSelect from '../common/SearchableSelect';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { leafAccounts } from '../../utils/postingAccounts';

const SegmentSettings: React.FC = () => {
    const toast = useToast();

    const isInstitutionAdmin = usePermission(PERMISSIONS.ACCOUNTING_SETTINGS_MANAGE);
    const isReadOnly = !isInstitutionAdmin;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [segments, setSegments] = useState<Segment[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);

    const [formData, setFormData] = useState<SegmentSettingsUpdate>({
        clearingAccountId: '',
        defaultSegmentId: '',
        segmentMandatory: false,
    });

    useEffect(() => {
        const loadAll = async () => {
            setLoading(true);
            try {
                const [settingsData, segmentsPage, loadedAccounts] = await Promise.all([
                    SegmentSettingsService.get(),
                    SegmentService.getByInstitution(),
                    AccountService.getAllAccounts(),
                ]);

                const loadedSegments: Segment[] = segmentsPage.content ?? [];

                setSegments(loadedSegments);

                // Sort accounts: INTER_SEGMENT_CLEARING ones first
                const sorted = [...loadedAccounts].sort((a, b) => {
                    const aIsClearing = a.postingClass === 'INTER_SEGMENT_CLEARING' ? 0 : 1;
                    const bIsClearing = b.postingClass === 'INTER_SEGMENT_CLEARING' ? 0 : 1;
                    if (aIsClearing !== bIsClearing) return aIsClearing - bIsClearing;
                    return a.code.localeCompare(b.code);
                });
                setAccounts(sorted);

                setFormData({
                    clearingAccountId: settingsData.clearingAccountId ?? '',
                    defaultSegmentId: settingsData.defaultSegmentId ?? '',
                    segmentMandatory: settingsData.segmentMandatory,
                });
            } catch (err) {
                toast.showApiError(err, 'Failed to load segment settings');
            } finally {
                setLoading(false);
            }
        };

        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const dto: SegmentSettingsUpdate = {
                clearingAccountId: formData.clearingAccountId || undefined,
                defaultSegmentId: formData.defaultSegmentId || undefined,
                segmentMandatory: formData.segmentMandatory,
            };
            await SegmentSettingsService.update(dto);
            toast.showSuccess('Settings saved');
        } catch (err) {
            toast.showApiError(err, 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box data-help-page="accounting.segmentSettings.page" sx={{ padding: '24px', maxWidth: 640 }}>
            <Typography variant="h5" gutterBottom>
                Segment Settings
            </Typography>

            {isReadOnly && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You have read-only access. Only Institution Admins may change these settings.
                </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                The default segment is applied to any entry line left without a segment. The inter-segment
                clearing account absorbs cross-segment residuals so each segment's books balance.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                For multi-segment cash splitting, classify cash/bank accounts as <strong>CASH</strong> and
                receivables as <strong>RECEIVABLE</strong> on the Chart of Accounts (Posting Class).
            </Alert>

            {/* Inter-Segment Clearing Account */}
            <AccountAutocomplete data-help="accounting.segmentSettings.field.clearing"
                accounts={leafAccounts(accounts)}
                value={formData.clearingAccountId ?? ''}
                onChange={(id) => setFormData({ ...formData, clearingAccountId: id })}
                label="Inter-Segment Clearing Account"
                disabled={isReadOnly}
                placeholder="None"
            />

            {/* Default Segment */}
            <SearchableSelect data-help="accounting.segmentSettings.field.defaultSegment"
                options={segments}
                value={formData.defaultSegmentId ?? ''}
                onChange={(id) => setFormData({ ...formData, defaultSegmentId: id })}
                getOptionLabel={(segment) => `${segment.code} — ${segment.name}`}
                label="Default Segment"
                placeholder="None"
                disabled={isReadOnly}
                sx={{ mt: 2, mb: 1 }}
            />

            {/* Segment Mandatory Switch */}
            <Box sx={{ mt: 2, mb: 1 }}>
                <FormControlLabel data-help="accounting.segmentSettings.field.mandatory"
                    control={
                        <Switch
                            checked={formData.segmentMandatory}
                            onChange={(e) => setFormData({ ...formData, segmentMandatory: e.target.checked })}
                            disabled={isReadOnly}
                            color="primary"
                        />
                    }
                    label="Require segment on entry lines"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 1 }}>
                    Master switch. When on, accounts marked "Required" must carry a segment on every posting (missing segments default to the Common segment). When off, segment enforcement is disabled for this institution.
                </Typography>
            </Box>

            {/* Save Button */}
            <Box sx={{ mt: 3 }}>
                <Button data-help="accounting.segmentSettings.save"
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={isReadOnly || saving}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Box>
        </Box>
    );
};

export default SegmentSettings;
